import { useCallback, useEffect, useRef, useState } from "react";
import { CaretRight } from "@phosphor-icons/react";

/** Righe di menu numerate in stile AS/400: selezione con mouse, frecce, campo Selezione e INVIO. */
export const As400Menu = ({ title, subtitle, rows, selected, onSelect, onOpen, onExit, testid }) => {
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const [aiuto, setAiuto] = useState(false);
  const [selezione, setSelezione] = useState("");
  const [errore, setErrore] = useState(false);

  const evidenzia = useCallback((testo) => {
    const idx = Number(testo) - 1;
    if (testo && idx >= 0 && idx < rows.length) onSelect(idx);
  }, [onSelect, rows.length]);

  const apri = useCallback(() => {
    const idx = selezione ? Number(selezione) - 1 : selected;
    if (idx >= 0 && idx < rows.length) {
      setSelezione("");
      setErrore(false);
      onOpen(idx);
    } else {
      setErrore(true);
    }
  }, [onOpen, rows.length, selected, selezione]);

  const scrivi = useCallback((testo) => {
    const pulito = testo.replace(/\D/g, "").slice(0, 3);
    setSelezione(pulito);
    setErrore(false);
    evidenzia(pulito);
  }, [evidenzia]);

  const onKey = useCallback((e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelezione(""); onSelect(Math.min(selected + 1, rows.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelezione(""); onSelect(Math.max(selected - 1, 0)); return; }
    if (e.key === "Home") { e.preventDefault(); setSelezione(""); onSelect(0); return; }
    if (e.key === "End") { e.preventDefault(); setSelezione(""); onSelect(rows.length - 1); return; }
    if (e.key === "Enter") { e.preventDefault(); apri(); return; }
    if (["Escape", "F3", "F8"].includes(e.key)) { e.preventDefault(); setSelezione(""); onExit(); return; }
    if (e.key === "F1") { e.preventDefault(); setAiuto((v) => !v); return; }
    if (e.key === "Backspace") { e.preventDefault(); scrivi(selezione.slice(0, -1)); return; }
    // le cifre non aprono subito: si accumulano nel campo Selezione fino a INVIO
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      scrivi(`${selezione}${e.key}`);
      inputRef.current?.focus();
    }
  }, [apri, onExit, onSelect, rows.length, scrivi, selected, selezione]);

  useEffect(() => { setSelezione(""); boxRef.current?.focus(); }, [title]);

  return (
    <div ref={boxRef} tabIndex={0} onKeyDown={onKey} data-testid={testid || "as400-menu"}
      className="w-full max-w-3xl rounded-sm border-0 lg:border border-border bg-transparent lg:bg-card shadow-none lg:shadow-lg outline-none focus:ring-0 lg:focus:ring-2 lg:focus:ring-primary/40">
      <div className="border-b border-border px-3 lg:px-5 py-3 lg:py-4 text-left lg:text-center">
        <div className="font-head text-lg lg:text-xl font-bold tracking-tight uppercase" data-testid="as400-title">{title}</div>
        {subtitle && <div className="mt-1 text-xs text-muted-foreground hidden lg:block">{subtitle}</div>}
      </div>

      <div className="py-2 lg:p-3 space-y-1.5 lg:space-y-0 font-mono text-[13px]">
        {rows.map((r, i) => (
          <button type="button" key={r.key} data-testid={r.testid}
            onClick={() => { setSelezione(""); onSelect(i); onOpen(i); }}
            onMouseEnter={() => onSelect(i)}
            aria-current={i === selected}
            className={`w-full flex items-center gap-3 px-3 py-3.5 lg:py-2 rounded-sm border text-left transition-colors duration-150
              ${i === selected ? `${r.tone?.sel || "bg-primary/15 border-primary"} font-bold`
              : "border-border lg:border-transparent bg-card lg:bg-transparent hover:bg-secondary/60"}`}>
            <span className={`w-6 shrink-0 tabular-nums text-right hidden lg:block ${r.tone?.text || "text-muted-foreground"}`}>{i + 1}</span>
            <span className={`h-5 lg:h-4 w-1 lg:w-0.5 shrink-0 rounded-sm ${r.tone?.bar || "bg-border"}`} />
            <span className="truncate uppercase text-sm lg:text-[13px]">{r.label}</span>
            <span className="flex-1 overflow-hidden text-muted-foreground/50 whitespace-nowrap hidden lg:block">
              {" ".padEnd(0)}{"·".repeat(200)}
            </span>
            <CaretRight size={13} weight="bold" className="shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>

      <form className="border-t border-border px-3 lg:px-5 py-3 flex items-center gap-2 font-mono text-[12px]"
        onSubmit={(e) => { e.preventDefault(); apri(); }}>
        <label htmlFor={`${testid || "as400"}-sel`} className="uppercase text-muted-foreground">Selezione ===&gt;</label>
        <input id={`${testid || "as400"}-sel`} ref={inputRef} value={selezione} onKeyDown={onKey}
          onChange={(e) => scrivi(e.target.value)} inputMode="numeric" autoComplete="off"
          data-testid="as400-selection-input" aria-label="Numero della funzione da aprire"
          className={`w-16 h-8 px-2 rounded-sm border bg-background text-center tabular-nums
            outline-none focus:ring-2 focus:ring-primary/40 ${errore ? "border-rose-500 text-rose-600" : "border-border"}`} />
        <button type="submit" data-testid="as400-selection-go"
          className="h-8 px-3 rounded-sm border border-border uppercase font-semibold hover:bg-secondary">
          Invio
        </button>
        <span className="ml-auto text-muted-foreground hidden lg:block">
          {errore ? `numero non valido (1-${rows.length})` : "digita il numero e premi INVIO"}
        </span>
      </form>

      <div className="border-t border-border px-5 py-3 hidden lg:flex flex-wrap items-center gap-3 font-mono text-[11px] text-muted-foreground">
        <button type="button" onClick={() => setAiuto((v) => !v)} data-testid="as400-help"
          className="hover:text-foreground">F1 = Guida</button>
        <button type="button" onClick={onExit} data-testid="as400-exit"
          className="hover:text-foreground">ESC / F8 = Indietro</button>
        <span className="ml-auto">INVIO = apri · ESC / F8 = indietro</span>
      </div>

      {aiuto && (
        <div className="border-t border-border px-5 py-3 font-mono text-[11px] text-muted-foreground space-y-1 hidden lg:block"
          data-testid="as400-help-box">
          <div>Digita il numero della funzione nel campo Selezione e premi INVIO (anche a due cifre).</div>
          <div>ESC, F3 o F8 tornano al livello precedente. Il clic del mouse apre direttamente la voce.</div>
        </div>
      )}
    </div>
  );
};

export default As400Menu;
